import React, { useCallback, useEffect, useState } from 'react';
import styles from './Resizer.module.css';
import { useUiStore, type PanelId } from '@/store/uiStore';

interface ResizerProps {
  panel: PanelId;
  orientation: 'horizontal' | 'vertical';
}

export function Resizer({ panel, orientation }: ResizerProps) {
  const [isResizing, setIsResizing] = useState(false);
  const setPanelSize = useUiStore((s) => s.setPanelSize);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Calculate new size depending on panel and mouse position
      if (panel === 'left') {
        setPanelSize(panel, e.clientX);
      } else if (panel === 'right') {
        setPanelSize(panel, window.innerWidth - e.clientX);
      } else {
        // We assume StatusBar is roughly 24px-28px at the very bottom
        setPanelSize(panel, window.innerHeight - e.clientY - 28);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    // Add a class to body to prevent selection and cursor flickering
    document.body.style.cursor = orientation === 'horizontal' ? 'col-resize' : 'row-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, panel, orientation, setPanelSize]);

  return (
    <div 
      className={`${styles.resizer} ${styles[orientation]} ${isResizing ? styles.resizing : ''}`}
      onMouseDown={handleMouseDown}
    />
  );
}
